import type { ContentState as IContentState } from 'app/common/types';
import keyBy from 'lodash/keyBy';
import uniq from 'lodash/uniq';
import { createSelector } from 'reselect';
import { ContentState } from '../common/utils/content-state.js';
import { LINKS, NAME, PREVIEW_SITES } from './constants.js';
import type {
  LiveLinksSlice,
  LiveLinksState,
  PreviewSite,
  PreviewSiteRoute,
  ShapePreview,
  SiteLiveLinks,
  SiteUsage
} from './types';

export const getLiveLinksState = (state: LiveLinksSlice): LiveLinksState => state[NAME];

const getContentId = (state: unknown, props: { contentId: string }): string => props.contentId;
const getShapeId = (state: unknown, props: { shapeId: string }): string => props.shapeId;

const defaultState = new ContentState();

export const selectSiteUsageState = createSelector(
  getLiveLinksState,
  getContentId,
  (state: LiveLinksState, contentId: string): IContentState<SiteUsage[]> => {
    return state.getIn([LINKS, contentId], defaultState).toJS();
  }
);

export const selectSiteUsage = createSelector(selectSiteUsageState, (state) => (state.data ? state.data : []));

export const selectSitePreviewState = createSelector(
  getLiveLinksState,
  (state): IContentState<ShapePreview[]> => state.get(PREVIEW_SITES).toJS()
);

export const selectSitePreviews = createSelector(selectSitePreviewState, (state) => (state.data ? state.data : []));

export const selectPreviewSitesByContentId = createSelector(selectSitePreviews, getShapeId, (sitePreviews, shapeId) => {
  const sitePreview = sitePreviews.find((sitePreview) => sitePreview.contentTypeId === shapeId);
  return sitePreview ? sitePreview.sites : [];
});

const nameGroup = '([a-zA-Z][a-zA-Z0-9.]*)';
const variableRegex = new RegExp(`:${nameGroup}|{${nameGroup}}`, 'g');

type PathMatcher = (path: string) => boolean;

function createPathMatcher(path: string): PathMatcher {
  if (path === '*') {
    return (): boolean => false;
  }

  const regex = new RegExp(`^${path.replace(variableRegex, '.+')}$`);
  return (path: string): boolean => Boolean(regex.exec(path));
}

function createRouteMatcher(routes: PreviewSiteRoute[]): (path: string) => PreviewSiteRoute {
  const routeMatchers: Array<[PathMatcher, PreviewSiteRoute]> = routes.map((route) => [
    createPathMatcher(route.path),
    route
  ]);

  return (path: string): PreviewSiteRoute => {
    const found = routeMatchers.find(([matcher]) => matcher(path));
    return found
      ? found[1]
      : {
          routeName: '*',
          path: '*',
          isPaginated: false
        };
  };
}

export function getLiveLinks(previewSites: PreviewSite[], siteUsages: SiteUsage[]): SiteLiveLinks[] {
  const previewSitesById = keyBy(previewSites, 'siteId');
  const usageSitesById = keyBy(siteUsages, 'siteId');
  const siteIds = uniq([...Object.keys(previewSitesById), ...Object.keys(usageSitesById)]);

  return siteIds.map((siteId: string): SiteLiveLinks => {
    const previewSite = previewSitesById[siteId];
    const usageSite = usageSitesById[siteId];
    const { siteTitle } = previewSite || usageSite;
    const result: SiteLiveLinks = { siteId, siteTitle, routes: [] };

    const routeMatcher = createRouteMatcher(previewSite ? previewSite.routes : []);

    if (usageSite) {
      result.deployedAt = usageSite.deployedAt;
      result.baseUrl = usageSite.baseUrl;
      result.routes = usageSite.usages.map((usage) => {
        const { routeName, isPaginated } = routeMatcher(usage.path);
        return {
          routeName,
          preview: routeName !== '*',
          path: usage.path,
          isPaginated
        };
      });
    } else if (previewSite) {
      result.routes = previewSite.routes
        .filter((route) => route.routeName !== '*')
        .map((route) => ({
          ...route,
          preview: true
        }));
    }

    if (previewSite) {
      result.token = previewSite.token;
    }

    return result;
  });
}

export const selectLiveLinks = createSelector(selectPreviewSitesByContentId, selectSiteUsage, getLiveLinks);
